import { queryOptions } from "@tanstack/react-query";
import { apiClient, orgHeader, unwrap } from "../../api/client";
import type { components } from "../../api/generated/vision-api";
import type { DashboardDocument } from "../dashboards/copy";

export type Home = components["schemas"]["Home"];
export type HomeLayer = "personal" | "organisation";

/**
 * Every /v1/home answer carries catalogue texts (`tile_updates[].name`) in the language asked for, so each call names
 * the app's language, as the device page's do.
 */
const homeHeader = (orgId: string, language: string) => ({ "Accept-Language": language, ...orgHeader(orgId) });

/** The cache key of the home in every language; invalidate this one. */
export const homeKey = (orgId: string) => ["org", orgId, "/v1/home"] as const;

/** The home page in effect (specs/home-page HOME-01 to HOME-03, HOME-06): the user's own, else the organisation's. */
export const homeQuery = (orgId: string, language: string) =>
  queryOptions({
    queryKey: [...homeKey(orgId), language],
    queryFn: () => unwrap(apiClient("customers").GET("/v1/home", { params: { header: homeHeader(orgId, language) } })),
  });

export function saveHome(orgId: string, language: string, layer: HomeLayer, document: DashboardDocument) {
  return unwrap(
    apiClient("customers").PUT("/v1/home", {
      params: { header: homeHeader(orgId, language) },
      body: { layer, document: { ...document } },
    }),
  );
}

export function resetHome(orgId: string, language: string, layer: HomeLayer) {
  return unwrap(
    apiClient("customers").DELETE("/v1/home", { params: { query: { layer }, header: homeHeader(orgId, language) } }),
  );
}

/**
 * Adds devices' home widgets to the home in effect, or to the user's own when they may not save the organisation's
 * (HOME-05, HOME-06); with `dismiss`, Not now: the devices are not offered again.
 */
export function addToHome(orgId: string, language: string, deviceIds: string[], dismiss = false) {
  return unwrap(
    apiClient("customers").POST("/v1/home/devices", {
      params: { header: homeHeader(orgId, language) },
      body: { device_ids: deviceIds, ...(dismiss ? { dismiss } : {}) },
    }),
  );
}

/**
 * "New home tile for <type>" (device-type-pages DTP-15, vision-api 0.47.0): Update replaces the values and trend of that
 * type's tiles on the home in effect; with `dismiss`, Not now hides it until the type's tile changes again.
 */
export function updateHomeTiles(orgId: string, language: string, typeId: string, dismiss = false) {
  return unwrap(
    apiClient("customers").POST("/v1/home/tile-updates", {
      params: { header: homeHeader(orgId, language) },
      body: { type_id: typeId, ...(dismiss ? { dismiss } : {}) },
    }),
  );
}
